import { ChevronDown, LogOut, Moon, ShieldCheck, Sun } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { Button } from '../components/ui/button.tsx';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover.tsx';
import { STATIC_DEMO } from '../lib/config.ts';
import { useT } from '../lib/i18n.tsx';
import { useLogout, useMe } from '../lib/queries.ts';
import { useTheme } from '../lib/theme.ts';
import { carryOver } from '../lib/url-state.ts';

/** Sun in dark mode (back to light), moon in light mode (to dark). */
function ThemeToggle() {
  const t = useT();
  const { theme, toggle } = useTheme();
  const label = theme === 'dark' ? t.nav.themeToLight : t.nav.themeToDark;
  return (
    <Button variant="ghost" size="icon" onClick={toggle} aria-label={label} title={label}>
      {theme === 'dark' ? (
        <Sun aria-hidden="true" className="size-5" />
      ) : (
        <Moon aria-hidden="true" className="size-5" />
      )}
    </Button>
  );
}

export function initialsOf(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase());
  return letters.join('') || '?';
}

/** Always-visible account menu: sign out never needs a scroll. */
function AccountMenu() {
  const t = useT();
  const me = useMe();
  const logout = useLogout();
  const { search } = useLocation();
  const [open, setOpen] = useState(false);
  // The static demo has no server, so there is no session to end.
  if (STATIC_DEMO || !me.data) return null;
  const name = me.data.displayName;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={t.nav.account} className="gap-2 px-2">
          <span
            aria-hidden="true"
            className="flex size-7 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
          >
            {initialsOf(name)}
          </span>
          <span className="hidden max-w-40 truncate md:inline">{name}</span>
          <ChevronDown aria-hidden="true" className="size-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 space-y-1 p-2">
        <p className="truncate px-2 py-1.5 text-xs text-muted-foreground">
          {t.nav.signedInAs(name)}
        </p>
        <PopoverClose asChild>
          <Button asChild variant="ghost" size="sm" className="w-full justify-start">
            <Link to={{ pathname: '/security', search: carryOver(new URLSearchParams(search)) }}>
              <ShieldCheck aria-hidden="true" className="size-4" />
              {t.security.navLabel}
            </Link>
          </Button>
        </PopoverClose>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-destructive"
          disabled={logout.isPending}
          onClick={() => logout.mutate()}
        >
          <LogOut aria-hidden="true" className="size-4" />
          {t.nav.signOut}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/** Sticky top bar on every screen size: the sidebar / menu toggle and the account menu. */
export function TopBar({ menu }: { menu?: ReactNode }) {
  const t = useT();
  return (
    <header className="z-10 flex h-14 shrink-0 items-center gap-1 border-b bg-card px-2 sm:px-4">
      {menu}
      <span className="ml-1 truncate font-semibold lg:hidden">{t.app.name}</span>
      <div className="ml-auto flex items-center gap-1">
        <ThemeToggle />
        <AccountMenu />
      </div>
    </header>
  );
}
